import React from 'react';
import { Colors as C } from '../constants/Colors.js';
import { GENEROS_TREE } from '../constants/musicConstants.js';
import { DCard } from './components/MarketplaceAtoms.js';

export const GENEROS_PRINCIPALES = Object.keys(GENEROS_TREE);

function normalizeGenreToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

function matchGeneroPrincipal(name) {
  const n = normalizeGenreToken(name);
  if (!n) return null;
  const exact = GENEROS_PRINCIPALES.find(k => normalizeGenreToken(k) === n);
  if (exact) return exact;
  const partial = GENEROS_PRINCIPALES.find(k => {
    const kn = normalizeGenreToken(k);
    return n.includes(kn) || kn.includes(n);
  });
  if (partial) return partial;
  if (/rock|metal|punk|grunge/i.test(n)) return 'Rock / Metal';
  if (/pop|synth/i.test(n)) return 'Pop';
  if (/jazz|blues/i.test(n)) return 'Jazz / Blues';
  if (/electr|techno|house|edm/i.test(n)) return 'Electrónica';
  if (/hip.?hop|rap|trap|urban|reguet/i.test(n)) return 'Urbana';
  if (/folk|country|celtic/i.test(n)) return 'Folk / Tradicional';
  if (/clas/i.test(n)) return 'Clásica';
  if (/latin|flamenco|salsa|bossa/i.test(n)) return 'Latina / Regional';
  if (/españ|spanish/i.test(n)) return 'Española';
  if (/soul|funk|reggae|r&b/i.test(n)) return 'Reggae / Soul';
  if (/soundtrack|banda sonora|score/i.test(n)) return 'Banda Sonora / Score';
  return null;
}

function matchSubgenero(principal, name) {
  const n = normalizeGenreToken(name);
  if (!n || !principal) return '';
  const subs = GENEROS_TREE[principal] || [];
  const exact = subs.find(s => normalizeGenreToken(s) === n);
  if (exact) return exact;
  const partial = subs.find(s => {
    const sn = normalizeGenreToken(s);
    return sn.includes(n) || n.includes(sn);
  });
  if (partial) return partial;
  return String(name).trim();
}

/**
 * Mapea género de la IA a género principal + subgénero (árbol en `musicConstants`).
 * @param {string|null} texto - fallback libre (`genero`)
 * @param {object} [hints] - `generoPrincipal`, `generoSub` del JSON de Gemini
 */
export function aplicarGeneroDesdeIA(texto, setPrincipal, setSub, hints = {}) {
  const principalHint = hints.generoPrincipal ?? hints.genero_principal;
  const subHint = hints.generoSub ?? hints.genero_sub;

  if (principalHint) {
    const principal = matchGeneroPrincipal(principalHint);
    if (principal) {
      setPrincipal(principal);
      setSub(subHint ? matchSubgenero(principal, subHint) : '');
      return;
    }
  }

  if (subHint && !principalHint) {
    for (const k of GENEROS_PRINCIPALES) {
      const hit = matchSubgenero(k, subHint);
      if (hit && GENEROS_TREE[k].some(s => normalizeGenreToken(s) === normalizeGenreToken(hit))) {
        setPrincipal(k);
        setSub(hit);
        return;
      }
    }
  }

  if (texto == null || String(texto).trim() === '') return;
  const g = String(texto).trim();

  const parts = g.split(/[/|,;·]+/).map(p => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const principal = matchGeneroPrincipal(parts[0]);
    if (principal) {
      setPrincipal(principal);
      setSub(matchSubgenero(principal, parts.slice(1).join(' ')) || parts[1]);
      return;
    }
  }

  const asMain = matchGeneroPrincipal(g);
  if (asMain) {
    setPrincipal(asMain);
    setSub('');
    return;
  }

  for (const k of GENEROS_PRINCIPALES) {
    const hit = matchSubgenero(k, g);
    if (hit && GENEROS_TREE[k].some(s => normalizeGenreToken(s) === normalizeGenreToken(hit))) {
      setPrincipal(k);
      setSub(hit);
      return;
    }
  }

  setPrincipal('');
  setSub(g);
}

/** Aplica todos los campos devueltos por `analyzeImage` al formulario de publicar/colección. */
export function applyPublicarFieldsFromAi(fields, setters) {
  if (!fields || !setters) return;
  const { setArtista, setTitulo, setAnio, setSello, setPais, setFormato, setGenero, setGeneroSub } = setters;
  if (fields.artista) setArtista(fields.artista);
  if (fields.titulo) setTitulo(fields.titulo);
  if (fields.anio) setAnio(String(fields.anio));
  if (fields.sello) setSello(fields.sello);
  if (fields.pais) setPais(fields.pais);
  if (fields.formato) setFormato(fields.formato);
  if (fields.genero_principal || fields.genero_sub || fields.genero) {
    aplicarGeneroDesdeIA(fields.genero, setGenero, setGeneroSub, {
      generoPrincipal: fields.genero_principal,
      generoSub: fields.genero_sub,
    });
  }
}

export const inpBase = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

export function twoBtnStyle(active) {
  return {
    flex: 1,
    padding: '9px',
    borderRadius: 9,
    fontSize: 11,
    cursor: 'pointer',
    border: `0.5px solid ${active ? C.gold : 'rgba(255,255,255,0.12)'}`,
    background: active ? 'rgba(200,168,75,0.08)' : 'transparent',
    color: active ? C.gold : 'rgba(255,255,255,0.5)',
  };
}

/** Tipo: ítem suelto vs conjunto (misma UX que publicar). */
export function TipoPublicacionRow({ esConjunto, onChangeES }) {
  const btnBlock = active => ({
    ...twoBtnStyle(active),
    flex: '0 0 auto',
    minWidth: 108,
    maxWidth: 140,
    padding: '10px 8px',
    textAlign: 'center',
    lineHeight: 1.25,
  });
  const hint = {
    fontSize: 9,
    color: 'rgba(255,255,255,0.42)',
    lineHeight: 1.4,
    flex: 1,
    minWidth: 0,
    paddingTop: 2,
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Tipo *</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => onChangeES(false)} style={btnBlock(!esConjunto)}>
            💿 Individual
          </button>
          <span style={hint}>
            Un solo artículo en tu colección. El soporte (vinilo 12&quot;, CD…) lo indicas en <strong style={{ color: 'rgba(255,255,255,0.55)' }}>Formato</strong>.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => onChangeES(true)} style={btnBlock(esConjunto)}>
            🎵 Conjunto
          </button>
          <span style={hint}>Varios volúmenes o un box set como una sola ficha de colección.</span>
        </div>
      </div>
    </div>
  );
}

export function YesNoRow({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { k: 'no', label: 'No', v: false },
          { k: 'si', label: 'Sí', v: true },
        ].map(({ k, label: L, v }) => (
          <button key={k} type="button" onClick={() => onChange(v)} style={twoBtnStyle(value === v)}>
            {L}
          </button>
        ))}
      </div>
    </div>
  );
}

export const ESTADO_OPTS = [
  { value: '', label: 'Selecciona el estado...' },
  { value: 'M', label: 'M — Mint' },
  { value: 'VG+', label: 'VG+ — Muy bueno' },
  { value: 'VG', label: 'VG — Bueno' },
  { value: 'G', label: 'G — Aceptable' },
  { value: 'P', label: 'P — Deteriorado' },
];

export const FORMATO_OPTS = ['Vinilo 12"', 'Vinilo 7"', 'CD', 'Casete'];

const estadoGuia = [
  ['M', 'Mint — Como nuevo. Nunca usado.'],
  ['VG+', 'Muy bueno — Casi perfecto, uso mínimo, sin ruido.'],
  ['VG', 'Bueno — Alguna marca, leve crepitar en silencio.'],
  ['G', 'Aceptable — Escuchable con marcas y ruido.'],
  ['P', 'Deteriorado — Muy marcado, valor solo coleccionable.'],
];

export function EstadoBloque({ value, onChange, notaConjunto }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{notaConjunto ? 'Estado general *' : 'Estado del artículo *'}</div>
      {notaConjunto && (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6, lineHeight: 1.4 }}>
          Estado del disco en peor condición del conjunto
        </div>
      )}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...inpBase, marginBottom: 8 }}
        onFocus={e => {
          e.target.style.borderColor = C.gold;
        }}
        onBlur={e => {
          e.target.style.borderColor = 'rgba(255,255,255,0.12)';
        }}
      >
        {ESTADO_OPTS.map(o => (
          <option key={o.value || 'e'} value={o.value} style={{ background: '#1a1d28', color: '#fff' }}>
            {o.label}
          </option>
        ))}
      </select>
      <DCard style={{ padding: 10 }}>
        {estadoGuia.map(([cd, desc]) => (
          <div key={cd} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
            <span
              style={{
                fontSize: 9,
                padding: '2px 7px',
                borderRadius: 8,
                background: 'rgba(200,168,75,0.15)',
                color: C.gold,
                flexShrink: 0,
                marginTop: 1,
              }}>
              {cd}
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>{desc}</span>
          </div>
        ))}
      </DCard>
    </div>
  );
}
